import React from "react";
import { Box, IconButton, TableCell, TableRow, Typography } from "@mui/material";
import { Link } from "react-router-dom-v5-compat";
import { useDateFormatting } from "shared-ui/contexts/Locale/useDateFormatting";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { AthleteEnergyExpenditureHistoryQuery } from "../../../../types";
import { HistoricalTableSourceRow } from "./HistoricalTableSourceRow";
import { getNavOrgAthleteWithTeamMealPlansMealPlan } from "../../../../pages/Auth/Org/Athlete/AthletePaths";

interface HistoricalTableRowProps {
  date: string;
  entries: AthleteEnergyExpenditureHistoryQuery["athleteEnergyExpenditureHistory"]["edges"];
  athleteId: string;
  teamId: string;
}

export const HistoricalTableRow: React.FC<HistoricalTableRowProps> = ({
  date,
  entries,
  athleteId,
  teamId,
}) => {
  const { formatDateWithLocale } = useDateFormatting();
  const cellStyle = {
    fontSize: 14,
    color: "#101828",
    borderBottom: "1px solid #eaecf0",
    py: 2,
  };

  // Parse date string as local date (not UTC) to avoid timezone shift
  const [year, month, day] = date.split('-').map(Number);
  const formattedDate = formatDateWithLocale(new Date(year, month - 1, day));

  // If only one source, render as a single row
  if (entries.length === 1) {
    const entry = entries[0];

    return (
      <TableRow hover>
        <TableCell sx={cellStyle}>{formattedDate}</TableCell>
        <TableCell sx={cellStyle}>{entry.source}</TableCell>
        <TableCell sx={cellStyle}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>{entry.mealPlanName || "—"}</span>
            {entry.mealPlanId && entry.mealPlanName && (
              <IconButton
                component={Link}
                to={getNavOrgAthleteWithTeamMealPlansMealPlan(athleteId, teamId, entry.mealPlanId)}
                size="small"
                sx={{ p: 0 }}
              >
                <OpenInNewIcon sx={{ fontSize: 14, color: "text.secondary" }} />
              </IconButton>
            )}
          </Box>
        </TableCell>
        <TableCell sx={cellStyle} align="right">
          {entry.tdee != null ? `${Math.round(entry.tdee).toLocaleString()} kcal` : "—"}
        </TableCell>
        <TableCell sx={cellStyle} align="right">
          {entry.activeEnergy != null ? `${Math.round(entry.activeEnergy).toLocaleString()} kcal` : "—"}
        </TableCell>
        <TableCell sx={cellStyle} align="right">
          {entry.restingEnergy != null ? `${Math.round(entry.restingEnergy).toLocaleString()} kcal` : "—"}
        </TableCell>
        <TableCell sx={cellStyle} align="right">
          {entry.targetDailyIntake
            ? `${Math.round(entry.targetDailyIntake).toLocaleString()} kcal`
            : "—"}
        </TableCell>
        <TableCell sx={cellStyle} align="right">
          {entry.loggedIntake > 0 ? `${Math.round(entry.loggedIntake).toLocaleString()} kcal` : "—"}
        </TableCell>
        <TableCell sx={cellStyle} align="right">
          {entry.targetDailyIntake === null || entry.energyBalance == null
            ? "—"
            : `${entry.energyBalance >= 0 ? "+" : ""}${Math.round(entry.energyBalance).toLocaleString()} kcal`}
        </TableCell>
      </TableRow>
    );
  }

  // If multiple sources, render parent row + child rows
  // Use first entry for shared data (meal plan, target, logged intake)
  const firstEntry = entries[0];

  return (
    <>
      {/* Parent row showing the date and shared data */}
      <TableRow hover>
        <TableCell sx={cellStyle}>
          {formattedDate}
        </TableCell>
        <TableCell sx={cellStyle}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: "text.secondary",
            }}
          >
            {entries.length} sources
          </Typography>
        </TableCell>
        <TableCell sx={cellStyle}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>{firstEntry.mealPlanName || "—"}</span>
            {firstEntry.mealPlanId && firstEntry.mealPlanName && (
              <IconButton
                component={Link}
                to={getNavOrgAthleteWithTeamMealPlansMealPlan(athleteId, teamId, firstEntry.mealPlanId)}
                size="small"
                sx={{ p: 0 }}
              >
                <OpenInNewIcon sx={{ fontSize: 14, color: "text.secondary" }} />
              </IconButton>
            )}
          </Box>
        </TableCell>
        <TableCell sx={cellStyle} align="right">
          —
        </TableCell>
        <TableCell sx={cellStyle} align="right">
          —
        </TableCell>
        <TableCell sx={cellStyle} align="right">
          —
        </TableCell>
        <TableCell sx={cellStyle} align="right">
          {firstEntry.targetDailyIntake
            ? `${Math.round(firstEntry.targetDailyIntake).toLocaleString()} kcal`
            : "—"}
        </TableCell>
        <TableCell sx={cellStyle} align="right">
          {firstEntry.loggedIntake > 0 ? `${Math.round(firstEntry.loggedIntake).toLocaleString()} kcal` : "—"}
        </TableCell>
        <TableCell sx={cellStyle} align="right">
          {firstEntry.targetDailyIntake === null || firstEntry.energyBalance == null
            ? "—"
            : `${firstEntry.energyBalance >= 0 ? "+" : ""}${Math.round(firstEntry.energyBalance).toLocaleString()} kcal`}
        </TableCell>
      </TableRow>

      {/* Child rows for each source */}
      {entries.map((entry, index) => (
        <HistoricalTableSourceRow key={`${entry.id}_${index}`} entry={entry} />
      ))}
    </>
  );
};
